import axios from "axios";
import type { AxiosResponse } from "axios";

const api = axios.create({
  baseURL: `${import.meta.env.VITE_LYRAZONE_BASE_URL}/admin/general-trade`,
  withCredentials: true,
});

const unwrapResponse = <T = any>(response: AxiosResponse<any>): T => {
  return response?.data?.data ?? response?.data;
};

const unwrapListResponse = <T = any>(response: AxiosResponse<any>): T[] => {
  const payload = unwrapResponse<any>(response);

  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  if (Array.isArray(payload?.rows)) {
    return payload.rows;
  }

  if (Array.isArray(payload?.records)) {
    return payload.records;
  }

  console.error("Expected an array from General Trade API:", payload);

  return [];
};

export const gtApi = {
  customers: () => api.get("/customers").then(unwrapListResponse),

  customer: (id: number) => api.get(`/customers/${id}`).then(unwrapResponse),

  createCustomer: (payload: any) =>
    api.post("/customers", payload).then(unwrapResponse),

  updateCustomer: (id: number, payload: any) =>
    api.put(`/customers/${id}`, payload).then(unwrapResponse),

  deleteCustomer: (id: number) =>
    api.delete(`/customers/${id}`).then(unwrapResponse),

  offlineProducts: () => api.get("/offline-products").then(unwrapListResponse),

  salesOrders: () => api.get("/sales-orders").then(unwrapListResponse),

  salesOrder: (id: number) =>
    api.get(`/sales-orders/${id}`).then(unwrapResponse),

  createSalesOrder: (payload: any) =>
    api.post("/sales-orders", payload).then(unwrapResponse),

  updateSalesOrder: (id: number, payload: any) =>
    api.put(`/sales-orders/${id}`, payload).then(unwrapResponse),

  deliveryOrders: () => api.get("/delivery-orders").then(unwrapListResponse),

  deliveryOrder: (id: number) =>
    api.get(`/delivery-orders/${id}`).then(unwrapResponse),

  createDeliveryOrder: (payload: any) =>
    api.post("/delivery-orders", payload).then(unwrapResponse),

  updateDeliveryOrderStatus: (
    id: number,
    status: "draft" | "submitted" | "hold" | "cancelled" | "deleted",
  ) =>
    api.patch(`/delivery-orders/${id}/status`, { status }).then(unwrapResponse),

  availableDeliveryOrdersForInvoice: () =>
    api
      .get("/sales-invoices/available-delivery-orders")
      .then(unwrapListResponse),

  salesInvoices: () => api.get("/sales-invoices").then(unwrapListResponse),

  salesInvoice: (id: number) =>
    api.get(`/sales-invoices/${id}`).then(unwrapResponse),

  createSalesInvoice: (payload: any) =>
    api.post("/sales-invoices", payload).then(unwrapResponse),

  updateSalesInvoice: (id: number, payload: any) =>
    api.put(`/sales-invoices/${id}`, payload).then(unwrapResponse),
  
  payments: () => api.get("/customer-payments").then(unwrapListResponse),

  openInvoices: (customerId: number) =>
    api.get(`/customers/${customerId}/open-invoices`).then(unwrapListResponse),

  createPayment: (payload: any) =>
    api.post("/customer-payments", payload).then(unwrapResponse),

  statement: (params: any) =>
    api.get("/customer-statement", { params }).then(unwrapResponse),

  pdfUrl: (
    type: "sales-orders" | "delivery-orders" | "sales-invoices",
    id: number,
    suffix = "",
  ) =>
    `${
      import.meta.env.VITE_LYRAZONE_BASE_URL
    }/admin/general-trade/${type}/${id}/pdf${suffix}`,
};
